/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// react library for routing
import { useLocation, Route, Switch, Redirect } from "react-router-dom";
// redux
import { useDispatch, useSelector } from "react-redux";
// reactstrap components
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
} from "reactstrap";
//firebase
import firebase from "firebase";
// react component used to create sweet alerts
import ReactBSAlert from "react-bootstrap-sweetalert";

// core components
import Sidebar from "components/Sidebar/Sidebar.js";
import Loading from "components/Misc/Loading";
import AuthHeader from "components/Headers/AuthHeader.js";

import routes from "routes.js";
import {
  getPerformanceData,
  createDataStructure,
  getLocationsAndChecklists,
  getUsers,
  setLimitReached,
  clearChecklists,
} from "actions/checklistActions";
import { clearFilters } from "actions/filterActions";

function Admin() {
  const [sidenavOpen, setSidenavOpen] = React.useState(true);
  const [unauthorizedUser, setUnauthorizedUser] = React.useState(false);
  const location = useLocation();
  const mainContentRef = React.useRef(null);
  // Call the store state
  const userState = useSelector(state => state.user);
  const checklistsState = useSelector(state => state.checklists);
  // Use dispatch
  const dispatch = useDispatch();

  const [alert, setAlert] = React.useState(false);

  React.useEffect(() => {
    if (
      document.documentElement &&
      document.scrollingElement &&
      mainContentRef.current
    ) {
      document.documentElement.scrollTop = 0;
      document.scrollingElement.scrollTop = 0;
      mainContentRef.current.scrollTop = 0;
    }
  }, [location]);
  // Set the listener for the checklists
  React.useEffect(async () => {
    try {
      if (userState.user && userState.user.uid) {
        if (!checklistsState.franchises) {
          await dispatch(getLocationsAndChecklists());
        }
        await dispatch(getPerformanceData());
        await dispatch(getUsers());
        dispatch(createDataStructure());
        if (
          checklistsState.pastChecklists &&
          checklistsState.pastChecklists.length < 7
        ) {
          dispatch(setLimitReached());
        }
      }
    } catch (error) {
      if (error.message.includes("permission-denied")) {
        setUnauthorizedUser(true);
      }
      throw new Error(error);
    }
  }, []);

  const getRoutes = routes => {
    return routes.map((prop, key) => {
      if (userState.user.role === "em" && prop.name === "Billing") return;
      if (prop.collapse) {
        return getRoutes(prop.views);
      }
      if (prop.layout === "/admin") {
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      } else {
        return null;
      }
    });
  };
  const getBrandText = path => {
    for (let i = 0; i < routes.length; i++) {
      if (location.pathname.indexOf(routes[i].layout + routes[i].path) !== -1) {
        return routes[i].name;
      }
    }
    return "Brand";
  };
  // toggles collapse between mini sidenav and normal
  const toggleSidenav = e => {
    if (document.body.classList.contains("g-sidenav-pinned")) {
      document.body.classList.remove("g-sidenav-pinned");
      document.body.classList.add("g-sidenav-hidden");
    } else {
      document.body.classList.add("g-sidenav-pinned");
      document.body.classList.remove("g-sidenav-hidden");
    }
    setSidenavOpen(!sidenavOpen);
  };
  const getNavbarTheme = () => {
    return location.pathname.indexOf("admin/alternative-dashboard") === -1
      ? "dark"
      : "light";
  };

  const logOut = async () => {
    dispatch(clearChecklists());
    dispatch(clearFilters());
    await firebase.auth().signOut();
  };

  React.useEffect(() => {
    if (!userState.user.cancelWarning) return;
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={`${userState.user.cancelWarning} days until records are deleted.`}
        onConfirm={() => setAlert(null)}
        confirmBtnBsStyle="primary"
        confirmBtnText="Close"
        btnSize=""
      >
        {`After ${userState.user.cancelWarning} days all checklists created by you, and the mobile app user's accesses will be removed permanently.`}
      </ReactBSAlert>
    )
  }, [])

  if (unauthorizedUser) {
    return (
      <div className="main-content">
        <AuthHeader title="Uh-Oh!" style={{minHeight: '100vh'}}/>
        <Container className="mt--8 pb-5">
          <Row className="justify-content-center">
            <Col lg="5" md="7">
              <Card className="bg-secondary border-0 mb-0">
                <CardHeader className="bg-transparent pb-5">
                  <div className="text-muted text-center mt-2 mb-3">
                    <h3>
                      It seems like you are not authorized to access this content.
                    </h3>
                  </div>
                </CardHeader>
                <CardBody className="px-lg-5 py-lg-5">
                  <div className="text-center text-muted mb-4">
                    <p>
                    Please get in touch with the administrator to get this figured out.
                    </p>
                  </div>
                  <Row
                    className="justify-content-center"
                    style={{ marginTop: "-0.6rem" }}
                  >
                    <Col xs="6">
                      <Button
                        className="mt-4"
                        color="info"
                        type="button"
                        onClick={logOut}
                      >
                        Back to Login
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (
    !checklistsState.franchises ||
    !checklistsState.users ||
    !checklistsState.statistics
  ) {
    return (
      <>
        <Loading active={true} />
      </>
    );
  }

  return (
    <>
      {alert}
      <Sidebar
        routes={routes}
        toggleSidenav={toggleSidenav}
        sidenavOpen={sidenavOpen}
        logo={{
          innerLink: "/",
          imgSrc: require("assets/img/icons/logo.png").default,
          imgAlt: "...",
        }}
      />
      <div className="main-content" ref={mainContentRef}>
        {/* <AdminNavbar
          theme={getNavbarTheme()}
          toggleSidenav={toggleSidenav}
          sidenavOpen={sidenavOpen}
          brandText={getBrandText(location.pathname)}
        /> */}
        <Switch>
          {getRoutes(routes)}
          <Redirect from="*" to="/admin/checklists" />
        </Switch>
      </div>
      {sidenavOpen ? (
        <div className="backdrop d-xl-none" onClick={toggleSidenav} />
      ) : null}
    </>
  );
}

export default Admin;
